import { FetchApi } from '../ApiFetch/ApiFetch';
import { ServiceType } from '../ApiFetch/ServiceType.types';
import { HTTPMethod } from '../ApiFetch/HTTPMethod.types';
import { CreateTextResponse, CreateBlobResponse, ResponseData } from '../ApiFetch/ApiResponse';
import { ApiUrls } from '../ApiConstants/ApiUrls';
import { OutputFileInfo } from '../../core/Types/SanityCheck.types';

export const fetchAllTestCases = async (): Promise<ResponseData> => {
    try {
        const url = ApiUrls.FETCH_ALL_TESTCASES;
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Get,
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
};

export type OutputFileWithType = {
    file: File;
    outputFileType: number;
};

export type TestCaseDetails = {
    testCaseName: string;
    jobId: string;
    clientId: string;
    inputFileId: number;
    inputFileName: string;
    outputFiles: Array<OutputFileInfo>;
    settingsUrl?: string;
};

export const uploadTestCaseInputOutput = async (
    jobId: string,
    clientId: string,
    testCaseName: string,
    inputFile: File,
    outputFiles: OutputFileWithType[],
): Promise<ResponseData> => {
    try {
        const url = ApiUrls.UPLOAD_INPUTOUTPUT_FILES;
        const formData = new FormData();
        formData.append('JobId', jobId);
        formData.append('ClientId', clientId);
        formData.append('TestCaseName', testCaseName);
        formData.append('InputFile', inputFile, inputFile.name);
        outputFiles.forEach((item, index) => {
        formData.append(`OutputFiles[${index}].File`, item.file, item.file.name);
        formData.append(`OutputFiles[${index}].FileType`, item.outputFileType.toString());
    });

        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Post,
            body: formData,
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
};

export const fetchTestCaseDetails = async (jobId: string, testcaseId: string): Promise<ResponseData> => {
    try {
        const url = `TestCase/${testcaseId}/fetch-testcase-details`;
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Get,
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
};

export const updateTestCaseDetails = async (
    testCaseId: string,
    testCaseName: string,
    jobId: string,
    inputFile?: File,
    inputFileName?: string,
    newOutputFiles?: OutputFileWithType[],
    deletedOutputFileTypes?: number[],
    isActive?: boolean
): Promise<ResponseData> => {
    try {
    const url = 'TestCase/update-testcase-details';
    const formData = new FormData();
    formData.append('TestCaseId', testCaseId);
    formData.append('TestCaseName', testCaseName);
    formData.append('JobId', jobId);
    formData.append('IsActive', isActive == true ? 'true' : isActive == false ? 'false' : 'null');

    if (inputFile) {
        formData.append('InputFile', inputFile, inputFile.name);
        formData.append('InputFileName', inputFile.name);
    } else if (inputFileName) {
        formData.append('InputFileName', inputFileName);
    }

    (newOutputFiles ?? []).forEach((item, index) => {
        if (!item?.file) return;

        formData.append(`NewOutputFiles[${index}].File`, item.file, item.file.name);
        formData.append(
            `NewOutputFiles[${index}].FileType`,
            item.outputFileType.toString()
        );
    });
    (deletedOutputFileTypes ?? []).forEach((fileType, index) => {
        formData.append(
            `DeletedOutputFileTypes[${index}]`,
            fileType.toString()
        );
    });
    const response = await FetchApi({
        endpoint: url,
        apiType: ServiceType.Common,
        method: HTTPMethod.Put,
        body: formData,
    });
    return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
};

export const runTestCase = async (jobId: string, testCaseId: string, clientId: string): Promise<ResponseData> => {
    try {
        const url = ApiUrls.RUN_TEST_CASE.replace('{id}', jobId.toString().toLowerCase());
        const formData = new FormData();
        formData.append('JobId', jobId);
        formData.append('ClientId', clientId);
        formData.append('TestCaseId', testCaseId);
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Import,
            method: HTTPMethod.Post,
            body: formData,
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
}

export const fetchCalculationProcessId = async (testCaseId: string): Promise<ResponseData> => {
    try {
        const url = ApiUrls.FETCH_CALCULATION_PROCESS_ID.replace('{testCaseId}', testCaseId.toLowerCase());
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Get,
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
}

export const resetTestCase = async (testCaseId: string): Promise<ResponseData> => {
    try {
        const url = ApiUrls.RESET_TESTCASE.replace('{testCaseId}', testCaseId.toLowerCase());
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Post,
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
}

export const updateTestCaseExecutionStatusUsingTestCaseId = async (testCaseId: string, 
    status: number): Promise<ResponseData> => {
        try {
        const url = ApiUrls.UPDATE_TESTCASE_STATUS_USING_TESTCASEID;
        const requestBody = {
            TestCaseId: testCaseId,
            Status: status
        };
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Put,
            body: JSON.stringify(requestBody),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return CreateTextResponse(response);
    } catch (e) {
        throw e;
    }
}

export const downloadTestCaseInputFile = async (testCaseId: string): Promise<FileComparisonDownloadResult> => {
    try {
        const url = ApiUrls.DOWNLOAD_IMPORTED_DATASET_USING_TESTCASEID.replace('{testCaseId}', testCaseId.toLowerCase());
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Get,
        });
        const contentDisposition = response.headers.get('content-disposition') ?? '';
        const fileName = getFileNameFromContentDisposition(contentDisposition);

        const blobResponse = await CreateBlobResponse(response);
        return { ...blobResponse, FileName: fileName };
    } catch (e) {
        throw e;
    }
}

export type FileComparisonDownloadResult = ResponseData & {
    FileName?: string;
};

const getFileNameFromContentDisposition = (contentDisposition: string): string | undefined => {
    if (!contentDisposition) {
        return undefined;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    }

    const regularMatch = contentDisposition.match(/filename=([^;\n]+)/i);
    if (regularMatch?.[1]) {
        return regularMatch[1].trim().replace(/^"|"$/g, '');
    }

    return undefined;
};

export const downloadFileComparisonFiles = async (calculationId: string): Promise<FileComparisonDownloadResult> => {
    try {
        const url = ApiUrls.DOWNLOAD_REGRESSION_FILES.replace('{calculationId}', calculationId.toLowerCase());
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Get,
        });
        const contentDisposition = response.headers.get('content-disposition') ?? '';
        const fileName = getFileNameFromContentDisposition(contentDisposition);

        const blobResponse = await CreateBlobResponse(response);
        return { ...blobResponse, FileName: fileName };
    } catch (e) {
        throw e;
    }
}

export const downloadFileComparisonErrorFile = async (calculationId: string): Promise<FileComparisonDownloadResult> => {
    try {
        const url = ApiUrls.DOWNLOAD_REGRESSION_ERROR_FILE.replace('{calculationId}', calculationId.toLowerCase());
        const response = await FetchApi({
            endpoint: url,
            apiType: ServiceType.Common,
            method: HTTPMethod.Get,
        });
        const contentDisposition = response.headers.get('content-disposition') ?? '';
        const fileName = getFileNameFromContentDisposition(contentDisposition);

        const blobResponse = await CreateBlobResponse(response);
        return { ...blobResponse, FileName: fileName };
    } catch (e) {
        throw e;
    }
}